import Bugsnag from '@bugsnag/js';
import BugsnagPerformance from '@bugsnag/browser-performance';
import { ReporterProvider } from '../contracts/ReporterProvider.js';

/**
 * Bugsnag Provider implementing ReporterProvider.
 * Wraps @bugsnag/js and @bugsnag/browser-performance.
 */
export class BugsnagProvider extends ReporterProvider {
  constructor() {
    super();
    this.name = 'bugsnag';
    this.isInitialized = false;
    this.bugsnagClient = null;
    this.performanceClient = null;
  }

  /**
   * Initialize Bugsnag and BugsnagPerformance.
   *
   * @param {Object} config
   * @param {string} config.apiKey
   * @param {string} config.releaseStage
   * @param {string[]} [config.enabledReleaseStages]
   * @param {string} [config.appVersion]
   * @param {Function} [config.onError] Optional custom error handler or filter callback
   */
  init(config) {
    if (this.isInitialized) {
      return this;
    }

    try {
      const bugsnagConfig = {
        apiKey: config.apiKey,
        releaseStage: config.releaseStage,
        enabledReleaseStages: config.enabledReleaseStages,
        appVersion: config.appVersion,
        onError: (event) => {
          // Ignore benign browser autoplay policy rejections (e.g. RADIO-294)
          const isAutoplayError = event.errors && event.errors.some((err) => {
            const isNotAllowed = err.errorClass === 'NotAllowedError';
            const msg = (err.errorMessage || '').toLowerCase();
            return isNotAllowed && (msg.includes('play()') || msg.includes('interact'));
          });

          if (isAutoplayError) {
            return false;
          }

          if (typeof config.onError === 'function') {
            return config.onError(event);
          }
        },
      };

      if (config.endpoints) {
        bugsnagConfig.endpoints = config.endpoints;
      }

      // Start error monitoring
      if (!Bugsnag.isStarted()) {
        this.bugsnagClient = Bugsnag.start(bugsnagConfig);
      } else {
        this.bugsnagClient = Bugsnag;
      }

      // Start performance monitoring
      try {
        BugsnagPerformance.start({
          apiKey: config.apiKey,
          releaseStage: config.releaseStage,
          enabledReleaseStages: config.enabledReleaseStages,
          appVersion: config.appVersion,
        });
        this.performanceClient = BugsnagPerformance;
      } catch (perfError) {
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('[BugsnagProvider] Failed to start BugsnagPerformance:', perfError);
        }
      }

      this.isInitialized = true;
    } catch (error) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[BugsnagProvider] Failed to initialize Bugsnag:', error);
      }
    }

    return this;
  }

  /**
   * Report an error to Bugsnag.
   *
   * @param {Error|string} error
   * @param {Function|Object} [metadataOrOnError]
   */
  notify(error, metadataOrOnError) {
    if (!this.isInitialized || !Bugsnag.isStarted()) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[BugsnagProvider (inactive)]', error);
      }
      return;
    }

    try {
      if (typeof metadataOrOnError === 'function') {
        Bugsnag.notify(error, metadataOrOnError);
      } else if (typeof metadataOrOnError === 'object' && metadataOrOnError !== null) {
        Bugsnag.notify(error, (event) => {
          Object.keys(metadataOrOnError).forEach((key) => {
            event.addMetadata(key, metadataOrOnError[key]);
          });
        });
      } else {
        Bugsnag.notify(error);
      }
    } catch (e) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[BugsnagProvider] Error while reporting:', e);
      }
    }
  }

  /**
   * Set user information.
   *
   * @param {Object} user - { id, name, email }
   */
  setUser(user = {}) {
    if (!this.isInitialized || !Bugsnag.isStarted()) return;
    try {
      Bugsnag.setUser(user.id || undefined, user.email || undefined, user.name || undefined);
    } catch (e) {
      // ignore
    }
  }

  /**
   * Clear identified user.
   */
  clearUser() {
    if (!this.isInitialized || !Bugsnag.isStarted()) return;
    try {
      Bugsnag.setUser();
    } catch (e) {
      // ignore
    }
  }

  /**
   * Add metadata.
   *
   * @param {string} section
   * @param {Object} data
   */
  addMetadata(section, data) {
    if (!this.isInitialized || !Bugsnag.isStarted()) return;
    try {
      Bugsnag.addMetadata(section, data);
    } catch (e) {
      // ignore
    }
  }

  /**
   * Clear metadata.
   *
   * @param {string} section
   * @param {string} [key]
   */
  clearMetadata(section, key) {
    if (!this.isInitialized || !Bugsnag.isStarted()) return;
    try {
      Bugsnag.clearMetadata(section, key);
    } catch (e) {
      // ignore
    }
  }

  /**
   * Leave a breadcrumb.
   *
   * @param {string} name
   * @param {Object} [metadata]
   * @param {string} [type]
   */
  leaveBreadcrumb(name, metadata, type) {
    if (!this.isInitialized || !Bugsnag.isStarted()) return;
    try {
      Bugsnag.leaveBreadcrumb(name, metadata, type);
    } catch (e) {
      // ignore
    }
  }

  /**
   * Return underlying clients.
   */
  getClient() {
    return {
      bugsnag: Bugsnag,
      performance: BugsnagPerformance,
    };
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/monitoring/providers/BugsnagProvider.js